import { Component, inject, signal, input, output, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SubscriptionPlan } from '../../services/subscription.service';
import { AuthService } from '../../services/auth.service';
import { ConfigService } from '../../services/config.service';
import { CompanyInfoService } from '../../services/company-info.service';

interface CheckoutRequest {
  planKey: string;
  autoRenewal: boolean;
  baseUrl: string;
}

interface CheckoutResponse {
  url: string;
}

@Component({
  selector: 'app-checkout',
  imports: [CommonModule, DecimalPipe, RouterLink],
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutComponent implements OnInit {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private configService = inject(ConfigService);
  private sanitizer = inject(DomSanitizer);
  private companyInfoService = inject(CompanyInfoService);

  plan = input.required<SubscriptionPlan>();
  visible = input.required<boolean>();
  closed = output<void>();

  autoRenewal = signal(false);
  paymentMethod = signal<'stripe' | 'paypal'>('stripe');
  loading = signal(false);
  error = signal<string | null>(null);
  termsAccepted = signal(false);

  ngOnInit() {
  }

  close() {
    this.closed.emit();
  }

  selectPaymentMethod(method: 'stripe' | 'paypal') {
    this.paymentMethod.set(method);
  }

  async proceedToCheckout() {
    if (!this.termsAccepted()) {
      this.error.set('Bitte akzeptieren Sie die AGBs');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const token = this.authService.getToken();
    if (!token) {
      this.error.set('Sie müssen eingeloggt sein');
      this.loading.set(false);
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    const request: CheckoutRequest = {
      planKey: this.plan().planKey,
      autoRenewal: this.autoRenewal(),
      baseUrl: window.location.origin
    };

    try {
      const endpoint = this.paymentMethod() === 'stripe' 
        ? `${environment.apiUrl}/payment/stripe/create-checkout-session`
        : `${environment.apiUrl}/payment/paypal/create-order`;

      const response = await this.http.post<CheckoutResponse>(
        endpoint,
        request,
        { headers }
      ).toPromise();

      if (response?.url) {
        // Redirect to payment provider
        window.location.href = response.url;
      } else {
        this.error.set('Fehler beim Erstellen der Checkout-Session');
        this.loading.set(false);
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      this.error.set(err.error?.error || 'Ein Fehler ist aufgetreten');
      this.loading.set(false);
    }
  }
}
